import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import type { MessageKey } from '../i18n/messages'
import { DateLisible, Nombre } from '../components/Nombre'
import { Badge, Button, Card, DataTable, FormActions, Grid, Stack, TextField, useToast } from '../components/ui'
import type { Column } from '../components/ui'
import { billingApi, toneOf } from './billingApi'
import type { InvoiceSummary } from './billingApi'

interface LigneSaisie {
  description: string
  quantity: string
  unitPrice: string
}

const LIGNE_VIDE: LigneSaisie = { description: '', quantity: '1', unitPrice: '' }
const nombre = (s: string) => Number(s.replace(/\s/g, '').replace(',', '.'))

/**
 * Les factures d'un client, sur sa fiche.
 *
 * « FACTURER LA PÉRIODE EN COURS » est le geste des clients créés avant la facturation, ou
 * dont la facture de renouvellement n'a pas pu partir (vendeur incomplet) : la base refuse de
 * facturer deux fois la même période, l'écran n'a donc pas à s'en souvenir.
 *
 * LA FACTURE LIBRE n'affiche pas de total calculé : il le serait en JavaScript, en virgule
 * flottante, et pourrait différer d'un millime de celui que la base émettra. Le total se lit
 * sur la facture émise.
 */
export function ClientInvoicesCard({ organizationId }: { organizationId: number }) {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const navigate = useNavigate()

  const token = session?.token ?? ''

  const [factures, setFactures] = useState<InvoiceSummary[]>([])
  const [saisie, setSaisie] = useState<LigneSaisie[] | null>(null)
  const [note, setNote] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const charger = useCallback(() => {
    if (token === '') return
    billingApi
      .listInvoices(token, organizationId)
      .then(setFactures)
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
  }, [token, organizationId, notify, describeError])

  useEffect(charger, [charger])

  const emettre = async (action: () => Promise<{ id: number }>) => {
    setEnvoi(true)
    try {
      const { id } = await action()
      notify(t('billing.client.issued'), 'succes')
      navigate(`/platform/invoices/${id}`)
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  const modifier = (i: number, cle: keyof LigneSaisie, valeur: string) =>
    setSaisie((s) => (s === null ? s : s.map((l, j) => (j === i ? { ...l, [cle]: valeur } : l))))

  const colonnes: Column<InvoiceSummary>[] = [
    {
      key: 'number',
      header: t('billing.col.number'),
      render: (f) => (
        <Link to={`/platform/invoices/${f.id}`}>
          <span dir="ltr">{f.number}</span>
        </Link>
      ),
    },
    { key: 'issuedOn', header: t('billing.col.issuedOn'), render: (f) => <DateLisible valeur={f.issuedOn} /> },
    { key: 'total', header: t('billing.col.total'), numeric: true, render: (f) => <Nombre valeur={f.totalTtc} decimales={3} /> },
    {
      key: 'balance',
      header: t('billing.col.balance'),
      numeric: true,
      render: (f) => (f.kind === 'invoice' ? <Nombre valeur={f.balance} decimales={3} /> : '—'),
    },
    {
      key: 'status',
      header: t('billing.col.status'),
      render: (f) => <Badge tone={toneOf(f.status)}>{t(`billing.status.${f.status}` as MessageKey)}</Badge>,
    },
  ]

  return (
    <Card title={t('billing.client.title')}>
      <Stack gap={3}>
        <DataTable columns={colonnes} rows={factures} rowKey={(f) => f.id} emptyLabel={t('billing.invoices.empty')} />

        {saisie === null ? (
          <FormActions>
            <Button variant="secondaire" onClick={() => setSaisie([{ ...LIGNE_VIDE }])}>
              {t('billing.client.newInvoice')}
            </Button>
            <Button onClick={() => emettre(() => billingApi.invoiceCurrentPeriod(token, organizationId))} pending={envoi}>
              {t('billing.client.currentPeriod')}
            </Button>
          </FormActions>
        ) : (
          <Stack gap={2}>
            <strong>{t('billing.client.newInvoice')}</strong>
            {saisie.map((l, i) => (
              <Grid key={i} min="10rem">
                <TextField label={t('billing.detail.description')} value={l.description} onChange={(v) => modifier(i, 'description', v)} required />
                <TextField label={t('billing.detail.quantity')} value={l.quantity} onChange={(v) => modifier(i, 'quantity', v)} ltr required />
                <TextField label={t('billing.detail.unitPrice')} value={l.unitPrice} onChange={(v) => modifier(i, 'unitPrice', v)} ltr required hint={t('billing.client.priceHint')} />
              </Grid>
            ))}
            <TextField label={t('billing.new.note')} value={note} onChange={setNote} />
            <FormActions>
              <Button variant="discret" onClick={() => setSaisie((s) => (s === null ? s : [...s, { ...LIGNE_VIDE }]))}>
                {t('billing.client.addLine')}
              </Button>
              <Button variant="secondaire" onClick={() => setSaisie(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() =>
                  emettre(() =>
                    billingApi.createInvoice(token, {
                      organizationId,
                      issuedOn: null,
                      lines: saisie.map((l) => ({ description: l.description.trim(), quantity: nombre(l.quantity), unitPrice: nombre(l.unitPrice) })),
                      note: note.trim() === '' ? null : note.trim(),
                    }),
                  )
                }
                disabled={saisie.some((l) => l.description.trim() === '' || l.unitPrice.trim() === '')}
                pending={envoi}
              >
                {t('billing.client.issue')}
              </Button>
            </FormActions>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
